import { Avatar, Box, Stack, Typography } from '@mui/material';
import { ALLOWED_FILE_TYPES } from 'Config';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { PlusBtn, UploadFile } from 'Images';
import { OTHER_ATTACHMENT_DOCUMENT_TYPE_IDS } from 'Hooks/useDocumentUpload';
import { FC, useRef } from 'react';

interface IFileUploadZone {
  onFileSelect: (files: FileList) => void;
  acceptedFileTypes?: string;
  hasUploadedFiles?: boolean;
  documentTypeName: string;
  documentTypeId: string;
  hasNonOtherAttachmentTypes: boolean;
}

export const FileUploadZone: FC<IFileUploadZone> = ({
  onFileSelect,
  acceptedFileTypes = ALLOWED_FILE_TYPES,
  hasUploadedFiles = false,
  documentTypeName,
  documentTypeId,
  hasNonOtherAttachmentTypes,
}) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);
  const isDesktop = useIsDesktop();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      onFileSelect(files);
    }
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      onFileSelect(files);
    }
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const isOtherOnlyContext =
    OTHER_ATTACHMENT_DOCUMENT_TYPE_IDS.includes(String(documentTypeId)) &&
    !hasNonOtherAttachmentTypes;

  const attachPrimaryLabel = isOtherOnlyContext
    ? dictionary.uploadFile.attachDocumentGeneric
    : dictionary.uploadFile.attachDocument(documentTypeName);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        border: '2px dashed #5B86EF70',
        borderRadius: '12px',
        backgroundColor: '#F6F8FE',
        padding: hasUploadedFiles ? '14px 12px' : '29px 12px',
        textAlign: 'center',
        transition: 'all 0.2s ease-in-out',
        pointerEvents: 'auto',
        cursor: 'pointer',
      }}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleButtonClick}
    >
      <Stack
        gap='12px'
        alignItems='center'
        justifyContent='center'
        direction='row'
        sx={{ pointerEvents: 'none', maxWidth: '100%' }}
      >
        <Avatar
          alt={dictionary.uploadFile.uploadFilesAlt}
          src={hasUploadedFiles ? PlusBtn : UploadFile}
          sx={{
            width: hasUploadedFiles ? '20px' : '48px',
            height: hasUploadedFiles ? '20px' : '48px',
          }}
        />
        <Stack width='auto' minWidth={isDesktop ? undefined : 0}>
          <Typography
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            variant={hasUploadedFiles ? 'body5' : 'body1'}
          >
            {attachPrimaryLabel}
          </Typography>
          {!hasUploadedFiles && (
            <Typography variant='body6'>
              {isDesktop
                ? dictionary.uploadFile.attachOneOrMore
                : dictionary.uploadFile.attachOneOrMoreMobile}
            </Typography>
          )}
        </Stack>

        <input
          ref={fileInputRef}
          type='file'
          accept={acceptedFileTypes}
          multiple
          hidden
          onChange={handleFileChange}
          onClick={(e) => e.stopPropagation()}
          aria-label={dictionary.uploadFile.selectFilesAriaLabel}
        />
      </Stack>
    </Box>
  );
};
